import Base from 'ember-simple-auth/authenticators/base';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import ENV from 'tio-employee/config/environment';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type SessionService from 'tio-employee/services/session';

/**
 * An authenticator to authenticate users against credentials stored in the Tio
 * database.
 *
 * The sign-in call is a raw `fetch` (not `store.request`) because it must read
 * the issued token from the `tio-auth-token` *response header* and inspect a
 * non-OK response without throwing — the same raw-`Response` access that kept
 * the legacy `api` service alive for this one call site, and the same pattern
 * the SAML authenticator already uses. Auth headers come from the shared
 * `buildTioAuthHeaders` helper (owner-free; pre-auth it omits `tio-auth-token`).
 *
 * @memberof authenticators
 */
class Database extends Base {
  @service declare session: SessionService;

  /**
   * Restores the session from a session data object; __will return a resolving
   * promise when there is a non-empty `access_token` in the session data__ and
   * a rejecting promise otherwise.
   *
   * @param {Object} data The data to restore the session from
   * @return {Promise} A promise that when it resolves results in the session becoming or remaining authenticated
   * @public
   */
  restore(data: unknown) {
    return new Promise((resolve, reject) => {
      if (!this._validateData(data)) {
        return reject('Could not restore session - "access_token" missing.');
      }

      return resolve(data);
    });
  }

  /**
   * Authenticates the user using the `email` and `password`.
   *
   * @param {string} email
   * @param {string} password
   * @return {Promise} A promise that when it resolves results in the session becoming authenticated
   * @public
   */
  async authenticate(email: string, password: string, initialData = {}) {
    const response = await fetch(`${ENV.apiHost}/user_logins/sign_in`, {
      method: 'POST',
      headers: buildTioAuthHeaders({ apiKey: ENV.apiKey, contentType: 'application/json' }),
      body: JSON.stringify({
        user_login: {
          email,
          password,
        },
      }),
    });

    if (response.ok) {
      const accessToken = response.headers.get('tio-auth-token');
      if (accessToken) {
        return {
          access_token: accessToken,
          ...initialData,
        };
      }

      // eslint-disable-next-line no-useless-assignment -- preserve {} default if json() throws before reassign
      let responseData: Record<string, unknown> = {};
      try {
        responseData = (await response.json()) as Record<string, unknown>;
      } catch (_error) {
        responseData = {};
      }

      return {
        ...initialData,
        ...responseData,
        noTransitionAfterAuth: true,
      };
    } else {
      throw await buildAuthError(response);
    }
  }
  /**
   * This method simply returns a resolving promise.
   *
   * @return {Promise} A promise that when it resolves results in the session being invalidated
   * @public
   */
  invalidate() {
    return new Promise<void>((resolve /*, reject*/) => {
      resolve();
    });
  }

  _validateData(data: unknown) {
    // see https://tools.ietf.org/html/rfc6749#section-4.2.2
    const sessionData = data as { access_token?: string };
    return !isEmpty(sessionData) && !isEmpty(sessionData.access_token);
  }
}

/**
 * Reproduce the error the old `api` service attached to non-OK sign-in
 * responses: an `Error` carrying the status text as `message` and the parsed
 * response body as `details`. The login form reads
 * `error.details?.error || error.message`, so both must be preserved.
 */
async function buildAuthError(response: Response): Promise<Error & { details?: object | string }> {
  const error = new Error(response.statusText || 'Unknown authentication error') as Error & {
    details?: object | string;
  };
  error.details = await parseErrorBody(response);
  return error;
}

async function parseErrorBody(response: Response): Promise<object | string> {
  const contentType = response.headers.get('Content-Type');
  try {
    if (
      contentType?.startsWith('application/json') ||
      contentType?.startsWith('application/vnd.api+json')
    ) {
      return (await response.json()) as object;
    }

    // Mirror the legacy `ApiService#parseResponse` content-type handling so the
    // login form (`error.details?.error || error.message`) shows the same thing
    // it did before this migration.
    if (contentType?.startsWith('text/plain')) {
      // Raw string → no `.error`, so the form falls back to `statusText`.
      return await response.text();
    }

    if (contentType?.startsWith('text/html')) {
      const text = await response.text();
      // Devise historically returned errors as `text/html` that were actually
      // plaintext. Real HTML returns a truncated snippet *string* (a string has
      // no `.error`, so the form still falls back to statusText — matching the
      // legacy UX; this authenticator layer has no `console`, so the snippet is
      // the debug signal carried on `error.details`). Plaintext-under-html keeps
      // the `{ error }` object the legacy path used so its message reaches the form.
      if (text.trim().startsWith('<')) {
        return `Unexpected HTML response from authentication endpoint: ${text.trim().slice(0, 500)}`;
      }
      return { error: text };
    }

    // Other content types (legacy returned a Blob, useless as an error detail):
    // surface the raw text so the form falls back to statusText.
    return await response.text();
  } catch (_error) {
    return {};
  }
}

export default Database;
